import {
    Box,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import {
    memo,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useDrop } from 'react-dnd'
import { IAssetSensorMarker } from '../interfaces'
import {
    setPresentConfiguringAssetSensorMarkers,
    setSelectedBuilding,
} from '../../../../redux/slices/facilityMonitoringSlice'
import {
    updateEntityInstance,
    updateEntityInstanceByDeletionOfProperties,
    uploadEntityInstanceImage,
} from '../../../../services/entityServices'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { ISelectorForRelations } from '../../../../services/apiInterfaces'
import { getNearestRelationNameByTargetEntityTemplateId } from '../../../../utils/RelationUtils'
import SensorCircle from './SensorCircle'
import SensorsDropDownModal from './SensorsDropDownModal'
import { enqueueSnackbar } from 'notistack'
import { useQueryClient } from '@tanstack/react-query'
import {
    floorLayoutDropTargets,
    MAX_FILE_SIZE,
    sensorCircleHeight,
    sensorCircleWidth,
    TemplatePermissionMode,
    URLSearchParamsKeys,
    UserPermissions,
    widthOfDragIndicator,
} from '../../../../constants/constants'
import {
    Delete,
    Edit,
    Fullscreen,
    FullscreenExit,
    Save,
} from '@mui/icons-material'
import ConfirmationDialog from '../../../Common/ConfirmationDialog'
import { useSearchParams } from 'react-router-dom'
import { IAsset } from '../../../../CustomHooks/FacilityMonitoring/getAssetsAndSensorsInBuilding'
import { useTranslation } from 'react-i18next'

interface ISensorFloorMapping {
    toggleFloorLayoutConainerExpand: () => void
    floorLayoutContainerExpand: boolean
    previouslyConfiguredSensorMarkers: IAssetSensorMarker[]
    loadingAssetsAndSensors: boolean
}

const SensorFloorMappingComponent = (
    props: ISensorFloorMapping,
): ReactElement => {
    const { t } = useTranslation()
    const {
        toggleFloorLayoutConainerExpand,
        floorLayoutContainerExpand,
        previouslyConfiguredSensorMarkers,
        loadingAssetsAndSensors,
    } = props
    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const {
        siteList,
        selectedBuilding,
        buildingEntityTemplateId,
        buildingFloorLayoutAttributeId,
        sensorEntityTemplateId,
        assetXCoordinateAttributeId,
        assetYCoordinateAttributeId,
        assetRelations,
        presentConfiguringAssetSensorMarkers,
    } = fmSlice
    const navList = useAppSelector((state) => state.nav.navList)
    const orgSlice = useAppSelector((state) => state.org)
    const user = useAppSelector((state) => state.user.user)
    const { org, permissions } = orgSlice
    const orgId = org?.id ? org.id : ''
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<string | null>(null)

    const [currentAssetSensorMarker, setCurrentAssetSensorMarker] =
        useState<IAssetSensorMarker | null>(null)
    const [showSensorsDropdownModal, setShowSensorsDropdownModal] =
        useState<boolean>(false)
    const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] =
        useState<boolean>(false)

    const [editMode, setEditMode] = useState(false)
    const [clearedMarkers, setClearedMarkers] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [searchParams] = useSearchParams()
    const selectedSiteUID =
        searchParams.get(URLSearchParamsKeys.selectedSiteUID) ?? siteList[0].uid

    const imageRef = useRef<HTMLDivElement>(null)
    const isSomeMarkersRemoved = clearedMarkers.length > 0
    const canEdit =
        siteList.find((site) => site.uid === selectedSiteUID)
            ?.permissionLevel !== TemplatePermissionMode.READ

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null
        //Enable and Test the if block belogic at later point of time thoroughly.
        // if (!file) {
        //     return
        // }
        setSelectedFile(file)

        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setPreview(reader.result as string)
            }
            reader.readAsDataURL(file)
        } else {
            setPreview(null)
        }
    }

    const syncWithServer = () => {
        Promise.all([
            queryClient.invalidateQueries({
                predicate: (query) => {
                    /**
                     * This will invalidate the queires with multiple search terms for selector API
                     */
                    return (
                        query.queryKey[0] === 'availableRelatedEntities' &&
                        query.queryKey.length > 4
                    )
                },
            }),
            queryClient.invalidateQueries({
                queryKey: [
                    'assetsAndSensorInBuilding',
                    selectedBuilding?.entityInstanceId,
                    buildingEntityTemplateId,
                    orgId,
                    user,
                ],
            }),
        ]).finally(() => {
            dispatch(setPresentConfiguringAssetSensorMarkers([]))
            setClearedMarkers([])
            setEditMode(false)
            setIsLoading(false)
        })
    }

    const saveChanges = async () => {
        setIsLoading(true)
        const isMarkersAdded = presentConfiguringAssetSensorMarkers.length > 0
        const fileUploadChanges = async () => {
            if (selectedFile && selectedFile?.size < MAX_FILE_SIZE) {
                const formData = new FormData()
                formData.append('entityFile', selectedFile, selectedFile.name)
                const filePath: {
                    type: string
                    payload: any
                } = await uploadEntityInstanceImage(orgId, formData)

                /** Client Side optimisation of updating the filePath url in selectedBuilding. Because we refer this
                 * URL in Building Attributes Table. For immediate reflection we are doing this client side optimisation update.
                 */
                const selectedBuildingCopy = {
                    ...selectedBuilding,
                    layout: filePath?.payload?.url || '',
                }
                dispatch(setSelectedBuilding(selectedBuildingCopy))
                const entityInstanceUpdatePayload = [
                    {
                        relations: [],
                        attributes: {
                            [buildingFloorLayoutAttributeId]:
                                filePath?.payload?.url || '',
                        },
                        entityInstanceId: selectedBuilding.entityInstanceId,
                    },
                ]
                try {
                    await updateEntityInstance(entityInstanceUpdatePayload)
                    enqueueSnackbar(t('floorLayoutUpdateSuccess'), {
                        variant: 'success',
                    })
                    setSelectedFile(null)
                    return true
                } catch {
                    enqueueSnackbar(t('errorUpdatingFloorLayout'), {
                        variant: 'error',
                    })
                    setSelectedFile(null)
                    return false
                }
            } else if (selectedFile && selectedFile?.size > 1048576) {
                enqueueSnackbar(t('fileSizeError'), {
                    variant: 'error',
                })
                return false
            }
            return false
        }

        const assetSensorAdditionUpdationChanges = async () => {
            if (isMarkersAdded) {
                const assetSensorRelationName =
                    getNearestRelationNameByTargetEntityTemplateId(
                        assetRelations,
                        sensorEntityTemplateId,
                        navList,
                    )
                const assetUpdatePayload =
                    presentConfiguringAssetSensorMarkers.map(
                        (assetSensorMarker) => {
                            const { assetInstanceId, sensorId, x, y } =
                                assetSensorMarker
                            return {
                                relations: [
                                    {
                                        relationName: assetSensorRelationName,
                                        childEntityInstanceId: sensorId,
                                        attributes: {},
                                    },
                                ],
                                attributes: {
                                    [assetXCoordinateAttributeId]: x,
                                    [assetYCoordinateAttributeId]: y,
                                },
                                entityInstanceId: assetInstanceId,
                            }
                        },
                    )
                try {
                    await updateEntityInstance(assetUpdatePayload)
                    enqueueSnackbar(t('sensorMappingSuccess'), {
                        variant: 'success',
                    })
                    return true
                } catch {
                    enqueueSnackbar(t('sensorMappingError'), {
                        variant: 'error',
                    })
                    return false
                }
            }
            return false
        }

        const sensorDeletionChanges = async () => {
            if (isSomeMarkersRemoved) {
                const sensorMarkersToBeDeleted: IAssetSensorMarker[] = []
                clearedMarkers.forEach((assetInstanceId) => {
                    const sensorMarker = previouslyConfiguredSensorMarkers.find(
                        (marker) => marker.assetInstanceId === assetInstanceId,
                    )
                    if (sensorMarker) {
                        sensorMarkersToBeDeleted.push(sensorMarker)
                    }
                })
                return await deleteMarkers(sensorMarkersToBeDeleted)
            }
            return false
        }

        await Promise.all([
            fileUploadChanges(),
            assetSensorAdditionUpdationChanges(),
            sensorDeletionChanges(),
        ])
            .then((results) => {
                const hasSuccess = results.some(Boolean)
                if (hasSuccess) {
                    syncWithServer() // Only call if at least one API call succeeded
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const undoChanges = () => {
        dispatch(setPresentConfiguringAssetSensorMarkers([]))
        if (selectedFile) {
            setSelectedFile(null)
            loadImage()
        }
        setClearedMarkers([])
        setEditMode(false)
    }

    const handleMarkerClear = (assetInstanceId: string) => {
        const isPresentConfiguring = presentConfiguringAssetSensorMarkers.some(
            (marker) => marker.assetInstanceId === assetInstanceId,
        )
        if (isPresentConfiguring) {
            dispatch(
                setPresentConfiguringAssetSensorMarkers(
                    presentConfiguringAssetSensorMarkers.filter(
                        (marker) => marker.assetInstanceId !== assetInstanceId,
                    ),
                ),
            )
        }
        const isPreviousConfigured = previouslyConfiguredSensorMarkers.some(
            (marker) => marker.assetInstanceId === assetInstanceId,
        )
        if (!isPresentConfiguring || isPreviousConfigured) {
            setClearedMarkers((prevCleared) => {
                return [...prevCleared, assetInstanceId]
            })
        }
    }

    const handleEditClick = () => {
        setEditMode(!editMode)
        setClearedMarkers([]) // Clear selections when entering/leaving edit mode
    }

    const deleteMarkers = async (
        sensorMarkersToBeDeleted: IAssetSensorMarker[],
    ): Promise<boolean> => {
        const payload = sensorMarkersToBeDeleted.map((marker) => {
            const { assetInstanceId, sensorId } = marker
            const assetSensorRelationName =
                getNearestRelationNameByTargetEntityTemplateId(
                    assetRelations,
                    sensorEntityTemplateId,
                    navList,
                )
            return {
                relations: [
                    {
                        relationName: assetSensorRelationName,
                        childEntityInstanceId: sensorId,
                        attributes: {},
                    },
                ],
                attributes: {
                    [assetXCoordinateAttributeId]: null,
                    [assetYCoordinateAttributeId]: null,
                },
                entityInstanceId: assetInstanceId,
            }
        })

        if (payload.length > 0) {
            try {
                await updateEntityInstanceByDeletionOfProperties(payload)
                enqueueSnackbar(t('removeSensorsSuccess'), {
                    variant: 'success',
                })
                return true
            } catch {
                enqueueSnackbar(t('removeSensorsError'), {
                    variant: 'error',
                })
                return false
            }
        }
        return false
    }

    const onConfirmDeleteSensorMarkers = async () => {
        setIsLoading(true)
        const shouldReload = await deleteMarkers(
            previouslyConfiguredSensorMarkers,
        )
        if (shouldReload) {
            syncWithServer()
        }
        setIsLoading(false)
        setOpenDeleteConfirmationDialog(false)
    }

    const loadImage = useCallback(() => {
        const imagePath = selectedBuilding?.layout || ''
        setPreview(imagePath)
    }, [selectedBuilding?.layout])

    useEffect(() => {
        if (selectedBuilding?.entityInstanceId) {
            loadImage()
        } else {
            setPreview(null)
        }
        return () => {
            dispatch(setPresentConfiguringAssetSensorMarkers([]))
            setClearedMarkers([])
        }
    }, [selectedBuilding?.entityInstanceId, dispatch, loadImage])

    const [, drop] = useDrop({
        accept: Object.keys(floorLayoutDropTargets), // Accept the 'SENSOR' drag type
        drop: (item: IAsset | IAssetSensorMarker, monitor) => {
            // console.log('droppedItem', item, monitor.getItemType())
            const dropItemType = monitor.getItemType()
            const clientOffset = monitor.getClientOffset()
            if (!clientOffset) return
            const { x: clientX, y: clientY } = clientOffset
            const imageRect = imageRef.current?.getBoundingClientRect()
            if (!imageRect) return

            const relativeX = (clientX - imageRect.left) / imageRect.width
            const relativeY = (clientY - imageRect.top) / imageRect.height

            if (dropItemType === floorLayoutDropTargets.ASSET) {
                /**
                 * This is the case when Asset is dragged from Assets table and dropped on the
                 * floor layout. i.e., source is Assets table, destination is floor layout.
                 */
                const sensor = (item as IAsset).relatedSensor
                const selectedAssetName: string = (item as IAsset)?.uid || ''
                if (sensor) {
                    const assetSensorMarker: IAssetSensorMarker = {
                        assetInstanceId: (item as IAsset).entityInstanceId,
                        assetName: selectedAssetName,
                        x: relativeX * 100,
                        y: relativeY * 100,
                        sensorId: sensor.entityInstanceId,
                    }
                    dispatch(
                        setPresentConfiguringAssetSensorMarkers([
                            ...presentConfiguringAssetSensorMarkers,
                            assetSensorMarker,
                        ]),
                    )
                } else {
                    setCurrentAssetSensorMarker({
                        assetInstanceId: (item as IAsset).entityInstanceId,
                        assetName: selectedAssetName,
                        x: relativeX * 100,
                        y: relativeY * 100,
                    })
                    setShowSensorsDropdownModal(true)
                }
            } else {
                /**
                 * This is the case when assetSensorMarker is rearranged on the floor layout.
                 * i.e., the source and destination is floor layout.
                 */
                let isUpdated = false
                const updatedPresentConfiguringAssetSensorMarkers =
                    presentConfiguringAssetSensorMarkers.map((sesnorMarker) => {
                        if (
                            sesnorMarker.sensorId ===
                            (item as IAssetSensorMarker).sensorId
                        ) {
                            isUpdated = true
                            const updatedMarker: IAssetSensorMarker = {
                                ...sesnorMarker,
                                x: relativeX * 100,
                                y: relativeY * 100,
                            }
                            return updatedMarker
                        } else {
                            return sesnorMarker
                        }
                    })
                dispatch(
                    setPresentConfiguringAssetSensorMarkers(
                        updatedPresentConfiguringAssetSensorMarkers,
                    ),
                )
                if (!isUpdated) {
                    const updatePresent = [
                        ...presentConfiguringAssetSensorMarkers,
                        {
                            ...item,
                            x: relativeX * 100,
                            y: relativeY * 100,
                        } as IAssetSensorMarker,
                    ]
                    dispatch(
                        setPresentConfiguringAssetSensorMarkers(updatePresent),
                    )
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    })

    const handleSubmit = (
        selectedSensor: ISelectorForRelations = {} as ISelectorForRelations,
    ) => {
        setShowSensorsDropdownModal(false)

        const assetSensorMarker: IAssetSensorMarker = {
            ...(currentAssetSensorMarker as IAssetSensorMarker),
            sensorId: selectedSensor.entityId,
        }
        dispatch(
            setPresentConfiguringAssetSensorMarkers([
                ...presentConfiguringAssetSensorMarkers,
                assetSensorMarker,
            ]),
        )

        setCurrentAssetSensorMarker(null)
    }

    const sensorMarkersOnImage = [...presentConfiguringAssetSensorMarkers]
    previouslyConfiguredSensorMarkers.forEach((previousMarker) => {
        const isAdded = presentConfiguringAssetSensorMarkers.some(
            (marker) => marker.sensorId === previousMarker.sensorId,
        )
        if (!isAdded) {
            sensorMarkersOnImage.push(previousMarker)
        }
    })

    const enableSaveAndUndoIcon =
        ((presentConfiguringAssetSensorMarkers &&
            presentConfiguringAssetSensorMarkers.length > 0) ||
            !!selectedFile ||
            isSomeMarkersRemoved) &&
        !isLoading &&
        !loadingAssetsAndSensors &&
        canEdit

    const enableEditIcon =
        (previouslyConfiguredSensorMarkers.length > 0 ||
            presentConfiguringAssetSensorMarkers.length > 0) &&
        !isLoading &&
        !loadingAssetsAndSensors &&
        canEdit

    const enableDeleteAllIcon =
        !!(preview && sensorMarkersOnImage.length > 0) &&
        !isLoading &&
        !loadingAssetsAndSensors &&
        canEdit

    const enableUpload =
        !isLoading &&
        !loadingAssetsAndSensors &&
        !!selectedBuilding.uid &&
        canEdit

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, pt: 0 }}>
            {permissions.includes(UserPermissions.ENTITY_WRITER) && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="fm-sensorfloor-input-imageupload"
                        type="file"
                        onChange={handleFileChange}
                        disabled={!enableUpload}
                        aria-label={t('uploadfloorimage')}
                    />
                    <label
                        htmlFor="fm-sensorfloor-input-imageupload"
                        style={{
                            cursor: enableUpload ? 'pointer' : 'not-allowed',
                        }}
                    >
                        <Tooltip title={t('upload')} placement="top">
                            <IconButton
                                id="fm-sensorfloor-button-upload"
                                color="primary"
                                component="span"
                                disabled={!enableUpload}
                                sx={{
                                    cursor: enableUpload
                                        ? 'pointer'
                                        : 'not-allowed',
                                    filter: enableUpload
                                        ? 'none'
                                        : 'grayscale(100%)',
                                }}
                                aria-label={t('uploadfloorplanimage')}
                            >
                                <img
                                    src="/assets/images/icons/image-upload-icon.svg"
                                    alt="upload-icon"
                                    loading="lazy"
                                    style={{
                                        width: '38px',
                                        height: '38px',
                                        margin: '8px',
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </label>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            sx={{
                                cursor: enableEditIcon
                                    ? 'default'
                                    : 'not-allowed',
                            }}
                        >
                            <Tooltip title={t('edit')} placement="top">
                                <IconButton
                                    id="fm-sensorfloor-button-edit"
                                    onClick={handleEditClick}
                                    disabled={!enableEditIcon}
                                    sx={{
                                        filter: enableEditIcon
                                            ? 'none'
                                            : 'grayscale(100%)',
                                        opacity: enableEditIcon ? 1 : 0.5,
                                        // width: '32px',
                                        minWidth: '32px',
                                        padding: '6px 8px',
                                    }}
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                cursor: enableSaveAndUndoIcon
                                    ? 'default'
                                    : 'not-allowed',
                            }}
                        >
                            <Tooltip title={t('save')} placement="top">
                                <IconButton
                                    id="fm-sensorfloor-button-save"
                                    color="primary"
                                    component="span"
                                    onClick={saveChanges}
                                    disabled={!enableSaveAndUndoIcon}
                                    sx={{
                                        filter: enableSaveAndUndoIcon
                                            ? 'none'
                                            : 'grayscale(100%)',
                                        opacity: enableSaveAndUndoIcon
                                            ? 1
                                            : 0.5,
                                        // width: '32px',
                                        minWidth: '32px',
                                    }}
                                >
                                    <Save />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                cursor: enableSaveAndUndoIcon
                                    ? 'default'
                                    : 'not-allowed',
                            }}
                        >
                            <Tooltip title={t('undo')} placement="top">
                                <IconButton
                                    id="fm-sensorfloor-button-undo"
                                    color="primary"
                                    component="span"
                                    onClick={undoChanges}
                                    disabled={!enableSaveAndUndoIcon}
                                    aria-label={t('uploadfloorimage')}
                                    sx={{
                                        filter: enableSaveAndUndoIcon
                                            ? 'none'
                                            : 'grayscale(100%)',
                                        opacity: enableSaveAndUndoIcon
                                            ? 1
                                            : 0.5,
                                        minWidth: '32px',
                                    }}
                                >
                                    <img
                                        src="/assets/images/icons/undo-icon.svg"
                                        loading="lazy"
                                        alt="undo-asset-sensor-bindings"
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                cursor: enableDeleteAllIcon
                                    ? 'default'
                                    : 'not-allowed',
                            }}
                        >
                            <Tooltip title={t('deleteAll')} placement="top">
                                <IconButton
                                    id="fm-sensorfloor-button-deleteall"
                                    color="primary"
                                    component="span"
                                    onClick={() =>
                                        setOpenDeleteConfirmationDialog(true)
                                    }
                                    disabled={!enableDeleteAllIcon}
                                    sx={{
                                        filter: enableDeleteAllIcon
                                            ? 'none'
                                            : 'grayscale(100%)',
                                        opacity: enableDeleteAllIcon ? 1 : 0.5,
                                        minWidth: '32px',
                                    }}
                                >
                                    <Delete color="error" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <IconButton
                            id="fm-sensorfloor-button-expand"
                            sx={{ color: '#00000081' }}
                            onClick={() => toggleFloorLayoutConainerExpand()}
                            aria-label={
                                floorLayoutContainerExpand
                                    ? t('exitfullscreenview')
                                    : t('enterfullscreenview')
                            }
                            title={
                                floorLayoutContainerExpand
                                    ? t('exitfullscreenview')
                                    : t('enterfullscreenview')
                            }
                        >
                            {floorLayoutContainerExpand ? (
                                <FullscreenExit />
                            ) : (
                                <Fullscreen />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            )}
            {(isLoading || loadingAssetsAndSensors) && <LinearProgress />}
            {preview ? (
                <Box mt={2} ref={imageRef} style={{ position: 'relative' }}>
                    {drop(
                        <img
                            src={preview}
                            alt={t('Selected file')}
                            width="100%"
                            height="100%"
                        />,
                    )}
                    {sensorMarkersOnImage.map((marker, index) => {
                        const isMarkerCleared = clearedMarkers.includes(
                            marker?.assetInstanceId ?? '',
                        )
                        return (
                            !isMarkerCleared && (
                                <Box
                                    key={`Sensior_${index}_${marker.sensorId}`}
                                >
                                    {editMode && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: `calc(${marker.y}% - ${sensorCircleHeight / 2}px)`,
                                                left: `calc(${marker.x}% - ${sensorCircleWidth / 2 - widthOfDragIndicator}px)`,
                                                transform:
                                                    'translate(60%, -60%)',
                                            }}
                                        >
                                            <ClearIcon
                                                color="error"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleMarkerClear(
                                                        marker.assetInstanceId,
                                                    )
                                                }
                                                id={`fm-sensor-floor-clear-icon-${marker.assetInstanceId}_${marker.sensorId}`}
                                            />
                                        </Box>
                                    )}
                                    <SensorCircle
                                        key={`Sensior_${index}_${marker.sensorId}`}
                                        sensorMarker={marker}
                                        isEditMode={editMode}
                                    />
                                </Box>
                            )
                        )
                    })}
                </Box>
            ) : (
                <Typography
                    component={'span'}
                    variant="body1"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        fontStyle: 'italic',
                        marginTop: '50px',
                    }}
                >
                    {t('uploadFloorLayout')}
                </Typography>
            )}
            {showSensorsDropdownModal && (
                <SensorsDropDownModal
                    showSensorsDropdownModal={showSensorsDropdownModal}
                    onClose={() => {
                        setShowSensorsDropdownModal(false)
                    }}
                    handleSubmit={handleSubmit}
                    droppedAsset={currentAssetSensorMarker}
                />
            )}
            {openDeleteConfirmationDialog && (
                <ConfirmationDialog
                    openDialog={openDeleteConfirmationDialog}
                    handleComfirm={onConfirmDeleteSensorMarkers}
                    handleClose={() => setOpenDeleteConfirmationDialog(false)}
                    DialogHeaderText={t('confirmDeleteHeadersensor')}
                    DialogBodyText={t('confirmDeleteBody')}
                />
            )}
        </Box>
    )
}

export const SensorFloorMapping = memo(SensorFloorMappingComponent)
SensorFloorMapping.displayName = 'SensorFloorMapping'
